import type { ReactNode } from "react";
import ChatListMediaThumb, {
  isChatListMediaType,
} from "./ChatListMediaThumb";
import TextTranslate from "../utils/TextTranslate";
import { truncateSentence } from "../utils/truncateSentence";
import {
  resolveMediaCaption,
} from "../utils/mediaMessageUrl";

type LastMessageLike = {
  message_type?: string | null;
  message_content?: string | null;
  message_thumbnail?: string | null;
  message_preview?: string | null;
  blur_hash?: string | null;
  blurHash?: string | null;
  media_url?: string | null;
  caption?: string | null;
  deleted_for_everyone?: boolean;
};

type Props = {
  message?: LastMessageLike | null;
  /** Fallback when not media — caller already rendered icons etc. */
  children?: ReactNode;
};

function mediaLabel(type: string) {
  if (type === "video") return "Video";
  if (type === "gif") return "Gif";
  return "Photo";
}

/**
 * Second row of chat list: optional media thumb + caption / type label.
 */
export default function ChatListLastMessagePreview({
  message,
  children,
}: Props) {
  if (!message) return <>{children}</>;

  const tombstone =
    message.deleted_for_everyone ||
    ["This message was deleted.", "🚫 This message was deleted!", "🚫 You deleted this message!"].includes(
      String(message.message_content || "").trim(),
    );
  if (tombstone) {
    return <>{children}</>;
  }

  const type = String(message.message_type || "");
  if (!isChatListMediaType(type)) {
    return <>{children}</>;
  }

  const caption =
    resolveMediaCaption(message as any) ||
    String(message.caption || "").trim();
  const thumb =
    String(message.message_thumbnail || "").trim() ||
    String(message.message_preview || "").trim();
  const blur = message.blur_hash || message.blurHash || null;

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <ChatListMediaThumb
        messageType={type}
        thumbUrl={thumb}
        blurHash={blur}
      />
      <span className="line-clamp-1 min-w-0">
        {caption
          ? truncateSentence(caption, 32)
          : (
            <TextTranslate text={mediaLabel(type)} />
          )}
      </span>
    </span>
  );
}
